"use client";

import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { resolveExamSessionForTimestamp } from "../lib/exam-session-client";
import type { ExamSession } from "../types/exam-session";

/**
 * 노트 작성 시각(createdAt) → 회차 라벨 변환 함수. 활성 회차면 null(뱃지 없음).
 * JCG-2059: 오답노트 회차 뱃지를 ProblemAnswer가 쓰이는 모든 곳에서 표시하기 위함.
 */
type ResolveNoteSession = (createdAtMs: number) => string | null;

const ExamSessionContext = createContext<ResolveNoteSession | null>(null);

/** ProblemAnswer가 prop으로 resolver를 못 받았을 때 fallback으로 사용 */
export function useResolveNoteSession(): ResolveNoteSession | null {
  return useContext(ExamSessionContext);
}

/** 앱이 자기 회차 목록을 돌려주는 함수. 앱마다 다른 Firestore 프로젝트를 읽는다. */
export type ListExamSessions = () => Promise<ExamSession[]>;

export interface ExamSessionProviderProps {
  children: React.ReactNode;
  /**
   * 회차 목록 조회. 앱별 서버 액션을 주입한다.
   * 넘기지 않으면 회차 개념이 없는 앱으로 보고 항상 null(뱃지 없음)을 돌려준다.
   */
  listExamSessions?: ListExamSessions;
}

/**
 * 전체 시험 회차를 한 번 로드(React Query 캐시)하여 노트별 회차 뱃지 resolver를 공급한다.
 * 루트 레이아웃에 마운트해 모든 페이지의 ProblemAnswer가 Context로 회차 라벨을 계산할 수 있게 한다.
 *
 * 조회 함수를 주입받는 이유: 공용 패키지가 특정 앱의 서버 액션을 직접 부르면
 * 빌드하는 앱과 무관하게 그 앱의 Firestore를 읽게 된다.
 */
export function ExamSessionProvider({
  children,
  listExamSessions,
}: ExamSessionProviderProps) {
  const { data } = useQuery({
    queryKey: ["exam-sessions"],
    queryFn: async () => (listExamSessions ? await listExamSessions() : []),
    enabled: Boolean(listExamSessions),
    staleTime: 1000 * 60 * 30, // 30분
    gcTime: 1000 * 60 * 60,
  });

  const sessions: ExamSession[] = useMemo(() => data ?? [], [data]);
  const activeId = useMemo(
    () => sessions.find((s) => s.isActive)?.id ?? null,
    [sessions]
  );

  const resolveNoteSession = useCallback<ResolveNoteSession>(
    (createdAtMs) => {
      const session = resolveExamSessionForTimestamp(sessions, createdAtMs);
      if (!session || session.id === activeId) return null;
      return session.displayName;
    },
    [sessions, activeId]
  );

  return (
    <ExamSessionContext.Provider value={resolveNoteSession}>
      {children}
    </ExamSessionContext.Provider>
  );
}
